import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { apiGet } from "@/lib/api";
import { SERVICE_META, SERVICE_ORDER } from "@/lib/serviceMeta";

/**
 * Home-page tile grid of all 10 service categories.
 * Sits between the pyramid solutions section and the tools preview —
 * acts as the PPC/SEO landing surface.
 */
export const ServicesTilesRow = () => {
  const [services, setServices] = useState([]);

  useEffect(() => {
    let cancelled = false;
    apiGet("/services")
      .then((d) => { if (!cancelled) setServices(d || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const bySlug = Object.fromEntries(services.map((s) => [s.slug, s]));
  const ordered = SERVICE_ORDER.map((slug) => bySlug[slug]).filter(Boolean);

  return (
    <section
      id="by-service"
      data-testid="services-tiles-row"
      className="py-20 sm:py-24 bg-lw-ivory-warm"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="max-w-2xl reveal">
          <span className="lw-eyebrow">Solutions by service</span>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-lw-navy leading-[1.1]">
            Ten service categories, <span className="italic">one strategy.</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-lw-muted">
            Life insurance, annuities, health, Medicare, LTC, disability,
            business, college, estate — plus a comprehensive planning umbrella
            that ties them together.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {ordered.map((s) => {
            const meta = SERVICE_META[s.slug] || {};
            const Icon = meta.Icon;
            return (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`home-service-tile-${s.slug}`}
                className="lw-card p-6 sm:p-7 flex flex-col group"
              >
                <div className="flex items-start justify-between">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center text-lw-navy"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(11,37,69,0.06) 0%, rgba(212,162,76,0.14) 100%)",
                    }}
                  >
                    {Icon && <Icon size={20} />}
                  </div>
                  {s.medicare_gated && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-lw-navy text-lw-ivory text-[10px] font-semibold uppercase tracking-[0.14em]">
                      Coming Soon
                    </span>
                  )}
                </div>
                <h3 className="mt-4 font-serif-display text-xl text-lw-navy leading-tight">
                  {s.name}
                </h3>
                <p className="mt-2 text-sm text-lw-muted leading-relaxed">
                  {meta.tile || s.hero_subhead}
                </p>
                <span className="mt-auto pt-5 inline-flex items-center gap-1.5 text-lw-navy font-semibold text-sm group-hover:gap-3 transition-all">
                  Explore <ArrowRight size={14} />
                </span>
              </Link>
            );
          })}
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/services"
            data-testid="home-view-all-services"
            className="btn-ghost-navy"
          >
            View all services <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ServicesTilesRow;
